import { supabase } from '../lib/supabase';

/**
 * Service for managing web push notifications
 */
export const pushNotificationService = {
  /**
   * Check if push notifications are supported
   */
  isSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  },

  /**
   * Get current notification permission status
   */
  getPermissionStatus() {
    if (!this.isSupported()) return 'unsupported';
    return Notification.permission;
  },

  /**
   * Request notification permission from user
   */
  async requestPermission() {
    if (!this.isSupported()) {
      throw new Error('Push notifications are not supported in this browser');
    }

    const permission = await Notification.requestPermission();
    return permission;
  },

  /**
   * Register service worker
   */
  async registerServiceWorker() {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service workers are not supported');
    }

    try {
      const registration = await navigator.serviceWorker?.register('/sw.js', {
        scope: '/'
      });
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      throw error;
    }
  },

  /**
   * Get service worker registration
   */
  async getServiceWorkerRegistration() {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service workers are not supported');
    }

    const registration = await navigator.serviceWorker?.ready;
    return registration;
  },

  /**
   * Subscribe to push notifications
   * Note: This requires a VAPID public key from your backend
   * For now, using a placeholder - you'll need to generate VAPID keys
   */
  async subscribeToPush(userId) {
    if (!this.isSupported()) {
      throw new Error('Push notifications are not supported');
    }

    // Check permission
    if (Notification.permission !== 'granted') {
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission denied');
      }
    }

    // Get service worker registration
    const registration = await this.getServiceWorkerRegistration();

    // Subscribe to push
    // Get VAPID public key from environment
    const vapidPublicKey = import.meta.env?.VITE_VAPID_PUBLIC_KEY;
    
    if (!vapidPublicKey || vapidPublicKey === 'your-vapid-public-key-here') {
      throw new Error('VAPID public key not configured. Please generate VAPID keys and add to environment variables.');
    }

    const subscription = await registration?.pushManager?.subscribe({
      userVisibleOnly: true,
      applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey)
    });

    // Save subscription to database
    await this.saveSubscription(userId, subscription);

    return subscription;
  },

  /**
   * Save push subscription to database
   */
  async saveSubscription(userId, subscription) {
    const subscriptionJson = subscription?.toJSON();

    const { data, error } = await supabase
      ?.from('push_subscriptions')
      ?.upsert({
        user_id: userId,
        endpoint: subscriptionJson?.endpoint,
        p256dh_key: subscriptionJson?.keys?.p256dh,
        auth_key: subscriptionJson?.keys?.auth,
        user_agent: navigator.userAgent,
        is_active: true,
        updated_at: new Date()?.toISOString()
      }, {
        onConflict: 'user_id,endpoint'
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Get user's push subscriptions
   */
  async getUserSubscriptions(userId) {
    const { data, error } = await supabase
      ?.from('push_subscriptions')?.select('*')?.eq('user_id', userId)?.eq('is_active', true);

    if (error) throw error;
    return data || [];
  },

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe(userId) {
    try {
      const registration = await this.getServiceWorkerRegistration();
      const subscription = await registration?.pushManager?.getSubscription();

      if (subscription) {
        await subscription?.unsubscribe();

        // Mark subscription as inactive in database
        const subscriptionJson = subscription?.toJSON();
        await supabase
          ?.from('push_subscriptions')
          ?.update({ is_active: false })
          ?.eq('user_id', userId)?.eq('endpoint', subscriptionJson?.endpoint);
      }

      return true;
    } catch (error) {
      console.error('Error unsubscribing:', error);
      throw error;
    }
  },

  /**
   * Check if user is currently subscribed
   */
  async isSubscribed() {
    if (!this.isSupported()) return false;

    try {
      const registration = await this.getServiceWorkerRegistration();
      const subscription = await registration?.pushManager?.getSubscription();
      return subscription !== null;
    } catch (error) {
      console.error('Error checking subscription:', error);
      return false;
    }
  },

  /**
   * Get current push subscription
   */
  async getCurrentSubscription() {
    if (!this.isSupported()) return null;

    try {
      const registration = await this.getServiceWorkerRegistration();
      const subscription = await registration?.pushManager?.getSubscription();
      return subscription;
    } catch (error) {
      console.error('Error getting subscription:', error);
      return null;
    }
  },

  /**
   * Helper function to convert VAPID key
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='?.repeat((4 - (base64String?.length % 4)) % 4);
    const base64 = (base64String + padding)?.replace(/\-/g, '+')?.replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData?.length; ++i) {
      outputArray[i] = rawData?.charCodeAt(i);
    }
    return outputArray;
  },

  /**
   * Test notification (for debugging)
   */
  async sendTestNotification() {
    if (!this.isSupported()) {
      throw new Error('Notifications not supported');
    }

    if (Notification.permission !== 'granted') {
      throw new Error('Notification permission not granted');
    }

    const registration = await this.getServiceWorkerRegistration();
    await registration?.showNotification('Test Notification', {
      body: 'This is a test notification from VitalTrack',icon: '/favicon.ico',badge: '/favicon.ico',tag: 'test',
      requireInteraction: false
    });
  }
};

export default pushNotificationService;